import { create } from 'zustand';
import { api, ApiError, UNAUTHORIZED_EVENT, type SessionUser } from '../lib/api';

/**
 * `offline`: the API isn't reachable (e.g. `npm run dev` without wrangler) — the editor
 * runs on local storage only, without accounts or admin.
 */
type AuthStatus = 'loading' | 'signedOut' | 'signedIn' | 'offline';

interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  aiEnabled: boolean;
  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  setAiEnabled: (enabled: boolean) => void;
}

/** Local deck cache keys; cleared on sign-out so the next person on this browser starts clean. */
const LOCAL_KEYS = ['slidesprint:lastDeckId', 'slidesprint:localDeck'];
const OWNER_KEY = 'slidesprint:cacheOwner';

/** Drop another user's cached deck if this browser was last used by someone else. */
function claimLocalCache(userId: string) {
  if (localStorage.getItem(OWNER_KEY) !== userId) {
    LOCAL_KEYS.forEach((k) => localStorage.removeItem(k));
    localStorage.setItem(OWNER_KEY, userId);
  }
}

async function loadAiEnabled(): Promise<boolean> {
  try {
    return (await api.publicSettings()).aiEnabled;
  } catch {
    return true;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  aiEnabled: true,

  init: async () => {
    window.addEventListener(UNAUTHORIZED_EVENT, () => {
      if (get().status === 'signedIn') set({ status: 'signedOut', user: null });
    });
    try {
      const { user } = await api.me();
      claimLocalCache(user.id);
      set({ status: 'signedIn', user, aiEnabled: user.mustChangePassword ? true : await loadAiEnabled() });
    } catch (err) {
      const signedOut = err instanceof ApiError && (err.status === 401 || err.status === 403);
      set({ status: signedOut ? 'signedOut' : 'offline', user: null });
    }
  },

  login: async (email, password) => {
    const { user } = await api.login(email, password);
    claimLocalCache(user.id);
    set({ status: 'signedIn', user, aiEnabled: user.mustChangePassword ? true : await loadAiEnabled() });
  },

  logout: async () => {
    await api.logout().catch(() => undefined);
    LOCAL_KEYS.forEach((k) => localStorage.removeItem(k));
    // Full reload drops the previous user's deck from memory.
    window.location.reload();
  },

  changePassword: async (currentPassword, newPassword) => {
    const { user } = await api.changePassword(currentPassword, newPassword);
    set({ user, aiEnabled: await loadAiEnabled() });
  },

  setAiEnabled: (aiEnabled) => set({ aiEnabled }),
}));
